import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import * as Sentry from '@sentry/vue';
import { useChunkErrorHandler } from "@/composables/useChunkErrorHandler";

const { handleChunkError, clearReloadFlag } = useChunkErrorHandler();

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // ============================================
    // RUTAS PÚBLICAS
    // ============================================
    {
      path: "/",
      name: "landing",
      component: () => import("../views/LandingPage.vue"),
    },
    {
      path: "/pricing",
      name: "pricing",
      component: () => import("../views/PricingPage.vue"),
    },
    {
      path: "/terminos",
      name: "terms",
      component: () => import("../views/TermsView.vue"),
    },
    {
      path: "/privacidad",
      name: "privacy",
      component: () => import("../views/PrivacyView.vue"),
    },
    {
      path: "/documentacion/api",
      name: "api-documentation",
      component: () => import("../views/ApiDocumentationView.vue"),
    },
    
    // ============================================
    // RUTAS DE AUTENTICACIÓN (Solo guests)
    // ============================================
    {
      path: "/login",
      name: "login",
      component: () => import("../views/auth/LoginView.vue"),
      meta: { requiresGuest: true },
    },
    {
      path: "/register",
      name: "register",
      component: () => import("../views/auth/RegisterView.vue"),
      meta: { requiresGuest: true },
    },
    {
      path: "/forgot-password",
      name: "forgot-password",
      component: () => import("../views/auth/ForgotPasswordView.vue"),
      meta: { requiresGuest: true },
    },
    {
      path: "/reset-password",
      name: "reset-password",
      component: () => import("../views/auth/ResetPasswordView.vue"),
      // No requiresGuest porque el usuario llega con sesión temporal de Supabase
    },
    
    // ============================================
    // RUTAS DE LA APLICACIÓN (Requieren auth)
    // ============================================
    {
      path: "/app",
      name: "app",
      redirect: "/app/projects",
      meta: { requiresAuth: true },
    },
    {
      path: "/app/projects",
      name: "projects",
      component: () => import("../views/app/ProjectsView.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/app/admin/chat",
      name: "admin-chat",
      component: () => import("../views/app/admin/ChatAdminView.vue"),
      meta: { requiresAuth: true, requiresChatSuperAdmin: true },
    },
    {
      path: "/app/settings",
      name: "settings",
      component: () => import("../views/app/SettingsLayout.vue"),
      redirect: "/app/settings/profile",
      meta: { requiresAuth: true },
      children: [
        {
          path: "profile",
          name: "settings-profile",
          component: () => import("../views/app/settings/ProfileView.vue"),
          meta: { requiresAuth: true },
        },
        {
          path: "plan",
          name: "settings-plan",
          component: () => import("../views/app/settings/PlanView.vue"),
          meta: { requiresAuth: true },
        },
        {
          path: "api",
          name: "settings-api",
          component: () => import("../views/app/settings/ApiKeysView.vue"),
          meta: { requiresAuth: true },
        },
        {
          path: "models",
          name: "settings-models",
          component: () => import("../views/app/settings/ModelsView.vue"),
          meta: { requiresAuth: true },
        },
        {
          path: "watts",
          name: "settings-watts",
          component: () => import("../views/app/settings/GetWattsView.vue"),
          meta: { requiresAuth: true },
        },
        {
          path: "tags",
          name: "settings-tags",
          component: () => import("../views/app/settings/TagsView.vue"),
          meta: { requiresAuth: true },
        },
        {
          path: "notifications",
          name: "settings-notifications",
          component: () => import("../views/app/settings/NotificationsView.vue"),
          meta: { requiresAuth: true },
        },
        {
          path: "colaboracion",
          name: "settings-collaboration",
          component: () => import("../views/app/settings/CollaborationView.vue"),
          meta: { requiresAuth: true },
        },
      ],
    },
    
    // ============================================
    // RUTAS DEL PROYECTO Y WIZARDS
    // ============================================
    {
      path: "/app/project/:projectId",
      name: "project-dashboard",
      component: () => import("../views/app/ProjectDashboardView.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/app/project/:projectId/kanban",
      name: "project-kanban",
      component: () => import("../views/app/ProjectKanbanView.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/app/project/:projectId/kanban/task/:taskId",
      name: "task-detail",
      component: () => import("../views/app/TaskDetailView.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/app/project/:projectId/compass",
      name: "project-compass",
      component: () => import("../views/app/CompassView.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/app/project/:projectId/compass/:documentId",
      name: "compass-document",
      component: () => import("../views/app/CompassDocumentView.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/app/project/:projectId/chat",
      name: "project-chat",
      component: () => import("../views/app/ProjectChatView.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/app/project/:projectId/:wizardId",
      name: "wizard",
      component: () => import("../views/app/WizardView.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/app/project/:projectId/:wizardId/:module",
      name: "wizard-module",
      component: () => import("../views/app/WizardView.vue"),
      meta: { requiresAuth: true },
    },
    
    // ============================================
    // RUTAS DEL KANBAN GLOBAL
    // ============================================
    {
      path: "/app/kanban",
      name: "global-kanban",
      component: () => import("../views/app/GlobalKanbanView.vue"),
      meta: { requiresAuth: true },
    },
    
    // ============================================
    // RUTA 404
    // ============================================
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      redirect: "/",
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth",
      };
    }
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  },
});

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Si la ruta requiere autenticación, asegurar que la sesión sea válida
  if (to.meta.requiresAuth) {
    // Verificar y refrescar sesión si es necesario
    const sessionValid = await authStore.ensureValidSession();
    
    if (!sessionValid) {
      // Sesión inválida o expirada, redirigir a login
      next({ name: "login", query: { redirect: to.fullPath } });
      return;
    }
  }

  // Si la ruta requiere ser invitado (login/register), verificar si ya está autenticado
  if (to.meta.requiresGuest) {
    // Primero verificar si hay sesión activa
    const sessionValid = await authStore.ensureValidSession();
    
    if (sessionValid) {
      // Usuario ya autenticado, redirigir a projects
      next({ name: "projects" });
      return;
    }
  }

  if (to.meta.requiresChatSuperAdmin) {
    const normalizedEmail = String(authStore.user?.email || "").trim().toLowerCase();
    if (normalizedEmail !== "carlosnocode@gmail.com") {
      next({ name: "projects" });
      return;
    }
  }

  next();
});

// Global error handler for chunk loading failures
router.onError((error, to) => {
  const handled = handleChunkError(error, {
    targetUrl: to?.fullPath,
    tags: { route: to?.name },
    extra: { targetRoute: to?.name, fullPath: to?.fullPath }
  });
  
  if (!handled) {
    // If not a chunk error or reload failed, let Sentry capture it
    Sentry.captureException(error, {
      tags: { errorType: 'router-error' },
      extra: { route: to?.fullPath }
    });
  }
});

// Clear reload flag on successful navigation
router.afterEach(() => {
  clearReloadFlag();
});

export default router;
